import { Button, ButtonProps, Popover, PopoverProps } from '@shopify/polaris';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

export type DropdownRef = { close: () => void };

export type DropdownProps = {
  activator: React.ReactElement;
  active?: boolean;
  className?: string;
  disabled?: boolean;
  disclosure?: ButtonProps['disclosure'];
  onRef?: (dropDown: DropdownRef) => void;
  fullWidth?: boolean;
  onToggleActive?(active: boolean): void;
} & Pick<PopoverProps, 'fullWidth' | 'fluidContent'>;

export const ButtonContainer = styled.div`
  .Polaris-Button {
    border-radius: 4px;
  }

  .Polaris-Button__Content {
    justify-content: space-between;
  }
`;

const Dropdown: FunctionComponent<DropdownProps> = ({
  activator,
  active = false,
  children,
  className,
  disabled,
  disclosure = 'select',
  fluidContent,
  fullWidth,
  onRef,
  onToggleActive,
}) => {
  const [isActive, setIsActive] = useState(active);

  useEffect(() => {
    setIsActive(active);
  }, [active]);

  useEffect(() => {
    if (onRef) {
      onRef({ close: onClose });
    }
  }, [onRef]);

  function onActive() {
    setIsActive(true);
  }

  function onClose() {
    setIsActive(false);
  }

  const toggleActive = useCallback(() => {
    setIsActive((active) => !active);
    onToggleActive && onToggleActive(!active);
  }, [active, onToggleActive]);

  return (
    <div className={className}>
      <Popover
        active={isActive}
        activator={
          <ButtonContainer>
            <Button
              onClick={onActive}
              disabled={disabled}
              disclosure={disclosure}
              fullWidth
            >
              {activator as unknown as string}
            </Button>
          </ButtonContainer>
        }
        onClose={toggleActive}
        ariaHaspopup={false}
        sectioned
        fullWidth={fullWidth}
        fluidContent={fluidContent}
        preventFocusOnClose
      >
        {children}
      </Popover>
    </div>
  );
};

export default Dropdown;
